import React, { useState, useEffect, useRef } from 'react';
import { salesApiService } from '../../api/salesApi';
import SalesCallModal from '../../components/modals/SalesCallModal';


const MONTHS = [
    { name: 'OCAK - JANUARY', value: 0 },
    { name: 'ŞUBAT - FEBRUARY', value: 1 },
    { name: 'MART - MARCH', value: 2 },
    { name: 'NİSAN - APRIL', value: 3 },
    { name: 'MAYIS - MAY', value: 4 },
    { name: 'HAZİRAN - JUNE', value: 5 },
    { name: 'TEMMUZ - JULY', value: 6 },
    { name: 'AĞUSTOS - AUGUST', value: 7 },
    { name: 'EYLÜL - SEPTEMBER', value: 8 },
    { name: 'EKİM - OCTOBER', value: 9 },
    { name: 'KASIM - NOVEMBER', value: 10 },
    { name: 'ARALIK - DECEMBER', value: 11 }
];

const RoadPlannerMonthly = () => {
    const [year] = useState(new Date().getFullYear());
    const [salesCalls, setSalesCalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);

    // Modal & Selection States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    // Drag Refs for reliable access in event handlers
    const dragStartRef = useRef(null);
    const dragCurrentRef = useRef(null);

    // State for visual updates
    const [dragStart, setDragStart] = useState(null);
    const [dragCurrent, setDragCurrent] = useState(null);

    useEffect(() => {
        fetchData();

        const handleGlobalMouseUp = () => {
            if (isDragging) {
                finishDrag();
            }
        };
        window.addEventListener('mouseup', handleGlobalMouseUp);
        return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }, [year, isDragging]); // eslint-disable-line react-hooks/exhaustive-deps

    const normalizeRepresentative = (name) => String(name || '').trim().toLocaleLowerCase('tr-TR');

    const fetchData = async () => {
        setLoading(true);
        try {
            const baseFilters = {
                start_date: `${year}-01-01 00:00:00`,
                end_date: `${year}-12-31 23:59:59`
            };

            let page = 1;
            let hasNext = true;
            const allCalls = [];

            // Endpoint paginated ise tum sayfalari topla
            while (hasNext) {
                const response = await salesApiService.getSalesCalls({ ...baseFilters, page });
                const listData = response?.data || response?.results || response;
                const pageCalls = Array.isArray(listData)
                    ? listData
                    : Array.isArray(listData?.results)
                        ? listData.results
                        : [];

                allCalls.push(...pageCalls);

                const nextFromPagination = response?.pagination?.next;
                const nextFromData = listData?.next;
                hasNext = Boolean(nextFromPagination || nextFromData);
                page += 1;

                // Güvenlik: beklenmedik döngüyü engelle
                if (page > 200) hasNext = false;
            }

            setSalesCalls(allCalls);

            // Temsilci isimlerini normalize ederek tekilleştir
            const representativeMap = new Map();
            allCalls.forEach((call) => {
                const rawName = String(call?.sales_representative || '').trim();
                if (!rawName) return;
                const key = normalizeRepresentative(rawName);
                if (!representativeMap.has(key)) {
                    representativeMap.set(key, rawName);
                }
            });
            const uniqueUsers = Array.from(representativeMap.values()).sort((a, b) => a.localeCompare(b, 'tr'));
            setUsers(uniqueUsers);

        } catch (error) {
            console.error("Error fetching sales calls:", error);
        } finally {
            setLoading(false);
        }
    };

    // Save Handling
    const handleSaveSalesCall = async (formData, selectedFiles) => {
        setLoading(true);
        try {
            let response;
            let salesCallId;

            if (selectedEntry && selectedEntry.id) {
                response = await salesApiService.updateSalesCall(selectedEntry.id, formData);
                salesCallId = selectedEntry.id;
            } else {
                response = await salesApiService.createSalesCall(formData);
                if (response.success) {
                    salesCallId = response.id || (response.data && response.data.id);
                }
            }

            if (response.success) {
                if (selectedFiles && selectedFiles.length > 0 && salesCallId) {
                    const fileData = new FormData();
                    selectedFiles.forEach(file => {
                        fileData.append('files', file);
                    });
                    await salesApiService.uploadSalesCallAttachments(salesCallId, fileData);
                }

                setIsModalOpen(false);
                setSelectedEntry(null);
                await fetchData();

            } else {
                alert(response.message || 'Error occurred during operation.');
            }
        } catch (err) {
            console.error(err);
            alert('Server error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAttachment = async (attachmentId) => {
        if (!window.confirm('Are you sure you want to delete this file?')) return;
        try {
            const response = await salesApiService.deleteSalesCallAttachment(attachmentId);
            if (response.success) {
                if (selectedEntry) {
                    setSelectedEntry(prev => ({
                        ...prev,
                        attachments: prev.attachments.filter(a => a.id !== attachmentId)
                    }));
                }
                fetchData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Grid Helpers
    const getDaysInMonth = (monthIndex) => {
        const date = new Date(year, monthIndex + 1, 0);
        const days = [];
        for (let i = 1; i <= date.getDate(); i++) {
            days.push(i);
        }
        return days;
    };

    const isWeekend = (day, monthIndex) => {
        const date = new Date(year, monthIndex, day);
        const d = date.getDay();
        return d === 0 || d === 6;
    };

    const getCellColor = (type) => {
        if (!type) return 'bg-blue-200';
        const t = type.toLowerCase();
        if (t.includes('fair') || t.includes('fuar')) return 'bg-[#f4b083] text-gray-900 border-l-4 border-l-orange-700';
        if (t.includes('ws') || t.includes('rs') || t.includes('workshop') || t.includes('roadshow')) return 'bg-[#92d050] text-gray-900 border-l-4 border-l-green-700';
        if (t.includes('sales call')) return 'bg-[#ffff00] text-gray-900 border-l-4 border-l-yellow-600';
        return 'bg-blue-200 text-gray-900 border-l-4 border-l-blue-600';
    };

    // REFACTOR: Her gün için o güne denk gelen eventleri listele
    const getRowCells = (user, monthIndex, daysInMonth) => {
        const cells = [];
        const normalizedUser = normalizeRepresentative(user);
        const userEvents = salesCalls.filter((call) =>
            normalizeRepresentative(call.sales_representative) === normalizedUser
        );

        for (let day = 1; day <= daysInMonth.length; day++) {
            const currentDt = new Date(year, monthIndex, day);
            const checkDt = new Date(currentDt.getFullYear(), currentDt.getMonth(), currentDt.getDate());

            const eventsForDay = userEvents.filter(e => {
                const s = new Date(e.start_date);
                const end = e.end_date ? new Date(e.end_date) : new Date(e.start_date);
                if (Number.isNaN(s.getTime()) || Number.isNaN(end.getTime())) return false;
                const sDate = new Date(s.getFullYear(), s.getMonth(), s.getDate());
                const eDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());

                return checkDt >= sDate && checkDt <= eDate;
            }).sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

            cells.push({ day, events: eventsForDay });
        }
        return cells;
    };

    // --- Drag & Drop Handlers ---

    const handleMouseDown = (day, month, user, e) => {
        if (e.button !== 0) return;
        setIsDragging(true);
        const data = { day, month, user };

        dragStartRef.current = data;
        dragCurrentRef.current = data;
        setDragStart(data);
        setDragCurrent(data);
    };

    const handleMouseEnter = (day, month, user) => {
        if (!isDragging) return;

        const currentStart = dragStartRef.current;
        if (currentStart?.month !== month || currentStart?.user !== user) return;

        const data = { day, month, user };

        dragCurrentRef.current = data;
        setDragCurrent(data);
    };

    const finishDrag = () => {
        const startState = dragStartRef.current;
        const currentState = dragCurrentRef.current;

        if (!startState || !currentState) {
            setIsDragging(false);
            return;
        }

        const startDay = Math.min(startState.day, currentState.day);
        const endDay = Math.max(startState.day, currentState.day);
        const month = startState.month;
        const user = startState.user;

        const startDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(startDay).padStart(2, '0')} 09:00`;
        const endDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDay).padStart(2, '0')} 18:00`;

        setSelectedEntry({
            start_date: startDateStr,
            end_date: endDateStr,
            sales_representative: user
        });
        setIsModalOpen(true);
        setIsDragging(false);

        setDragStart(null);
        setDragCurrent(null);
        dragStartRef.current = null;
        dragCurrentRef.current = null;
    };

    const handleEventClick = (event, e) => {
        e.stopPropagation();
        const formattedEntry = {
            ...event,
            start_date: event.start_date ? event.start_date.replace('T', ' ').substring(0, 16) : '',
            end_date: event.end_date ? event.end_date.replace('T', ' ').substring(0, 16) : '',
        };
        setSelectedEntry(formattedEntry);
        setIsModalOpen(true);
    };

    const isCellSelected = (day, month, user) => {
        if (!isDragging || !dragStart || !dragCurrent) return false;
        if (dragStart.month !== month || dragStart.user !== user) return false;

        const start = Math.min(dragStart.day, dragCurrent.day);
        const end = Math.max(dragStart.day, dragCurrent.day);
        return day >= start && day <= end;
    };

    return (
        <div className="p-6 bg-white min-h-screen overflow-x-auto dark:bg-gray-900 select-none">
            <SalesCallModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveSalesCall}
                onDeleteAttachment={handleDeleteAttachment}
                initialData={selectedEntry}
                availableCalls={salesCalls}
            />

            <h1 className="text-2xl font-bold text-center mb-6 text-gray-900 dark:text-white uppercase tracking-wider">
                AKKA HOTELS MARKETING PLAN {year}
            </h1>

            <div className="flex justify-start md:justify-end gap-6 text-xs font-bold mb-6 sticky top-0 bg-white dark:bg-gray-900 z-30 p-2 border-b dark:border-gray-700">
                <div className="flex items-center gap-2"><div className="w-5 h-5 bg-red-600 border border-gray-400"></div> Cumartesi & Pazar</div>
                <div className="flex items-center gap-2"><div className="w-5 h-5 bg-[#f4b083] border border-gray-400"></div> Fair/Fuar</div>
                <div className="flex items-center gap-2"><div className="w-5 h-5 bg-[#92d050] border border-gray-400"></div> WS-RS</div>
                <div className="flex items-center gap-2"><div className="w-5 h-5 bg-[#ffff00] border border-gray-400"></div> Sales Call</div>
            </div>

            <div className="flex flex-col gap-10">
                {MONTHS.map((month) => {
                    const days = getDaysInMonth(month.value);
                    if (users.length === 0) return null;

                    return (
                        <div key={month.value} className="border-2 border-gray-800 dark:border-gray-500 shadow-sm">
                            <div className="bg-[#9bc2e6] text-black font-extrabold text-center py-1.5 border-b-2 border-gray-800 text-sm tracking-wide">
                                {month.name}
                            </div>

                            <table className="w-full border-collapse table-fixed text-[11px]">
                                <colgroup>
                                    <col className="w-48 min-w-[12rem]" /> {/* Min width fixed for user col */}
                                    {days.map(d => <col key={d} />)} {/* Auto width */}
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className="border border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-48 min-w-[12rem] sticky left-0 z-20"></th>
                                        {days.map(d => {
                                            const isWknd = isWeekend(d, month.value);
                                            return (
                                                <th
                                                    key={d}
                                                    className={`border border-gray-600 border-b-2 h-8 text-center ${isWknd ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                                                >
                                                    {d}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user) => {
                                        const cells = getRowCells(user, month.value, days);
                                        return (
                                            <tr key={user} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                                <td className="border-r-2 border-b border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-bold px-2 py-1 truncate sticky left-0 z-10 w-48 min-w-[12rem] text-xs h-20 align-middle">
                                                    {user}
                                                </td>
                                                {cells.map((cell) => {
                                                    const isSelected = isCellSelected(cell.day, month.value, user);
                                                    return (
                                                        <td
                                                            key={cell.day}
                                                            className={`border border-gray-400 h-20 align-top p-0.5 relative group ${isSelected ? 'bg-blue-200 dark:bg-blue-900' : ''}`}
                                                            onMouseDown={(e) => handleMouseDown(cell.day, month.value, user, e)}
                                                            onMouseEnter={() => handleMouseEnter(cell.day, month.value, user)}
                                                        >
                                                            <div className="w-full h-full overflow-y-auto custom-scrollbar flex flex-col gap-0.5 max-h-[4.8rem]">
                                                                {cell.events.map(ev => (
                                                                    <div
                                                                        key={ev.id}
                                                                        onClick={(e) => handleEventClick(ev, e)}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        className={`text-[9px] px-0.5 py-0.5 rounded shadow-sm text-gray-900 font-semibold cursor-pointer whitespace-nowrap overflow-hidden text-ellipsis hover:opacity-80 ${getCellColor(ev.type)}`}
                                                                        title={`${ev.type} - ${ev.agency || ''} (${ev.city || ''})`}
                                                                    >
                                                                        {ev.type}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
            {loading && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded-lg shadow-lg font-semibold">İşlem Yapılıyor...</div>
                </div>
            )}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 2px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgba(156, 163, 175, 0.5);
                    border-radius: 20px;
                }
            `}</style>
        </div>
    );
};

export default RoadPlannerMonthly;
